// frontend/src/components/UI/ComingSoon.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wrench } from 'lucide-react';

/**
 * ComingSoon - Professional placeholder for features under development
 * 
 * @param {Object} props
 * @param {React.ComponentType} props.icon - Lucide icon component
 * @param {string} props.title - Feature name
 * @param {string} props.description - Brief description of what's coming
 * @param {Array<{icon: React.ComponentType, title: string, description: string}>} props.features - Optional feature previews
 */
const ComingSoon = ({ icon: Icon, title, description, features = [] }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full text-center">
        {/* Icon with subtle animation */}
        <div className="relative inline-flex items-center justify-center mb-8">
          <div className="absolute inset-0 bg-blue-100 rounded-full animate-pulse opacity-50" style={{ transform: 'scale(1.3)' }} />
          <div className="relative w-24 h-24 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center border border-blue-200 shadow-sm">
            <Icon className="w-12 h-12 text-blue-900" />
          </div>
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-amber-800 text-sm font-medium mb-6">
          <Wrench className="w-4 h-4" />
          Under Development
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {title}
        </h1>

        {/* Description */}
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          {description}
        </p>

        {/* Feature previews (optional) */}
        {features.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm text-left"
                >
                  <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <FeatureIcon className="w-5 h-5 text-gray-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-500">{feature.description}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="btn btn-primary"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Footer note */}
        <p className="mt-8 text-sm text-gray-400">
          We're working hard to bring this feature to you soon.
        </p>
      </div>
    </div>
  );
};

export default ComingSoon;

